import { useCallback, useState } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { invalidateTracksCache } from './cache';

export type TrackRecertificationSummary = {
  processed: number;
  certified: number;
  failed: number;
};

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Recomputes one track from its latest stored check results. */
export const useRecertifyTrack = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const recertify = useCallback(
    async (trackId: string): Promise<TrackRecertificationSummary> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `plugin://pulse/tracks/${encodeURIComponent(trackId)}/recertify`,
          { method: 'POST' },
        );
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to recertify track: ${response.status}`,
          );
        }
        const { summary } = (await response.json()) as {
          summary: TrackRecertificationSummary;
        };
        invalidateTracksCache();
        return summary;
      } catch (error_) {
        const requestError = error_ as Error;
        setError(requestError);
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { recertify, loading, error };
};
